<template>
  <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="min-width:960px; max-width:95vw">
      <q-bar class="bg-deep-orange-8 text-white disc-detail-bar">
        <q-icon name="timeline" size="sm" />
        <div class="disc-detail-titles">
          <span v-if="projectName" class="disc-detail-project" :title="projectName">
            <q-icon name="apartment" size="11px" class="q-mr-xs" />{{ projectName }}
          </span>
          <span class="disc-detail-work" :title="data.workName">{{ data.workName }}</span>
        </div>
        <q-space />
        <q-btn flat dense size="sm" icon="content_copy" @click="copyGuid">
          <q-tooltip>Скопировать GUID</q-tooltip>
        </q-btn>
        <q-btn dense flat icon="close" v-close-popup />
      </q-bar>

      <q-card-section class="q-pa-sm">
        <div class="text-caption text-grey-7 q-mb-xs">
          GUID: <span class="text-weight-medium">{{ data.workGuid }}</span>
        </div>

        <div v-if="hasSmeta" class="disc-smeta-bar">
          <div class="disc-smeta-bar__lbl">По смете (всего по работе):</div>
          <div class="disc-smeta-bar__cells">
            <div class="disc-smeta-bar__cell disc-smeta-bar__cell--ours">
              <div class="disc-smeta-bar__h">Наши</div>
              <div class="disc-smeta-bar__v">
                СМР <b>{{ fmtDisc(smeta.our_smr) }}</b>
                <span class="disc-smeta-bar__sep">/</span>
                ТМЦ <b>{{ fmtDisc(smeta.our_mat) }}</b>
              </div>
            </div>
            <div class="disc-smeta-bar__cell disc-smeta-bar__cell--usp">
              <div class="disc-smeta-bar__h">УСП</div>
              <div class="disc-smeta-bar__v">
                СМР <b>{{ fmtDisc(smeta.usp_smr) }}</b>
                <span class="disc-smeta-bar__sep">/</span>
                ТМЦ <b>{{ fmtDisc(smeta.usp_mat) }}</b>
              </div>
            </div>
            <div class="disc-smeta-bar__cell disc-smeta-bar__cell--delta">
              <div class="disc-smeta-bar__h">Δ</div>
              <div class="disc-smeta-bar__v">
                СМР <b :class="discDeltaClass(smeta.d_smr)">{{ fmtDiscDelta(smeta.d_smr) }}</b>
                <span class="disc-smeta-bar__sep">/</span>
                ТМЦ <b :class="discDeltaClass(smeta.d_mat)">{{ fmtDiscDelta(smeta.d_mat) }}</b>
              </div>
            </div>
          </div>
        </div>

        <q-markup-table dense bordered separator="cell" class="disc-detail-table">
          <thead>
            <tr>
              <th rowspan="2" style="width:110px" class="disc-th-info">Период</th>
              <th colspan="2" class="disc-th-ours">Наши данные (₸)</th>
              <th colspan="2" class="disc-th-usp">Данные УСП (₸)</th>
              <th colspan="2" class="disc-th-delta">Разница</th>
              <th rowspan="2" class="disc-th-info" style="min-width:130px">Контрагент</th>
              <th rowspan="2" class="disc-th-info" style="width:120px">Отправлено</th>
              <th rowspan="2" class="disc-th-info" style="width:60px">
                Отправок
                <q-icon name="info_outline" size="11px" class="q-ml-xs text-grey-5" />
                <q-tooltip>Сколько раз отправляли в УСП в этом периоде</q-tooltip>
              </th>
            </tr>
            <tr>
              <th class="disc-th-ours" style="width:100px">СМР</th>
              <th class="disc-th-ours" style="width:100px">ТМЦ</th>
              <th class="disc-th-usp" style="width:100px">СМР</th>
              <th class="disc-th-usp" style="width:100px">ТМЦ</th>
              <th class="disc-th-delta" style="width:100px">СМР</th>
              <th class="disc-th-delta" style="width:100px">ТМЦ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="data.loading">
              <td colspan="10" class="q-pa-md">
                <div class="flex flex-center" style="min-height:60px; gap:8px">
                  <q-spinner-dots size="2em" color="deep-orange" />
                  <span class="text-grey">Загрузка...</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="!data.rows.length">
              <td colspan="10" class="text-center q-pa-md text-grey">Нет данных по периодам</td>
            </tr>
            <template v-else>
              <tr v-for="(r, i) in data.rows" :key="i"
                  :class="{ 'disc-detail-diff': Math.abs(r.delta_smr) > 1 || Math.abs(r.delta_materials) > 1 }">
                <td class="text-weight-medium">{{ discPeriodLabel(r.period) }}</td>
                <td class="text-right disc-cell-ours">{{ fmtDisc(r.our_smr) }}</td>
                <td class="text-right disc-cell-ours">{{ fmtDisc(r.our_materials) }}</td>
                <td class="text-right disc-cell-usp">{{ fmtDisc(r.usp_smr) }}</td>
                <td class="text-right disc-cell-usp">{{ fmtDisc(r.usp_materials) }}</td>
                <td class="text-right" :class="discDeltaClass(r.delta_smr)">{{ fmtDiscDelta(r.delta_smr) }}</td>
                <td class="text-right" :class="discDeltaClass(r.delta_materials)">{{ fmtDiscDelta(r.delta_materials) }}</td>
                <td :title="r.contragent_name" class="ellipsis" style="max-width:180px">{{ r.contragent_name || '—' }}</td>
                <td class="text-center text-caption">{{ discFmtDateTime(r.last_sent_at) }}</td>
                <td class="text-center">
                  <span :class="(r.sent_count || 0) > 1 ? 'disc-sent-count disc-sent-count--many' : 'disc-sent-count'">
                    {{ r.sent_count || 0 }}
                  </span>
                </td>
              </tr>
              <tr class="disc-detail-total">
                <td class="text-weight-bold">Итого</td>
                <td class="text-right text-weight-bold disc-cell-ours">{{ fmtDisc(summary.our_smr) }}</td>
                <td class="text-right text-weight-bold disc-cell-ours">{{ fmtDisc(summary.our_mat) }}</td>
                <td class="text-right text-weight-bold disc-cell-usp">{{ fmtDisc(summary.usp_smr) }}</td>
                <td class="text-right text-weight-bold disc-cell-usp">{{ fmtDisc(summary.usp_mat) }}</td>
                <td class="text-right text-weight-bold" :class="discDeltaClass(summary.d_smr)">{{ fmtDiscDelta(summary.d_smr) }}</td>
                <td class="text-right text-weight-bold" :class="discDeltaClass(summary.d_mat)">{{ fmtDiscDelta(summary.d_mat) }}</td>
                <td></td>
                <td></td>
                <td class="text-center text-weight-bold">{{ summary.sent_count }}</td>
              </tr>
            </template>
          </tbody>
        </q-markup-table>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';

export default {
  name: 'PpmDiscrepancyDetailDialog',
  props: {
    modelValue: Boolean,
    data: { type: Object, default: () => ({ show: false, loading: false, workGuid: '', workName: '', rows: [] }) },
    projectName: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  setup() {
    const { fmtDisc, fmtDiscDelta, discDeltaClass, discPeriodLabel, discFmtDateTime } = useFormatters();
    return { fmtDisc, fmtDiscDelta, discDeltaClass, discPeriodLabel, discFmtDateTime };
  },
  computed: {
    summary() {
      const rows = this.data.rows;
      let our_smr = 0, our_mat = 0, usp_smr = 0, usp_mat = 0, sent_count = 0;
      for (const r of rows) {
        our_smr    += r.our_smr || 0;
        our_mat    += r.our_materials || 0;
        usp_smr    += r.usp_smr || 0;
        usp_mat    += r.usp_materials || 0;
        sent_count += r.sent_count || 0;
      }
      return { our_smr, our_mat, usp_smr, usp_mat, sent_count, d_smr: our_smr - usp_smr, d_mat: our_mat - usp_mat };
    },
    smeta() {
      const r0 = (this.data.rows || [])[0] || {};
      const our_smr = Number(r0.our_smeta_smr || 0);
      const our_mat = Number(r0.our_smeta_mat || 0);
      const usp_smr = Number(r0.usp_smeta_smr || 0);
      const usp_mat = Number(r0.usp_smeta_mat || 0);
      return { our_smr, our_mat, usp_smr, usp_mat, d_smr: our_smr - usp_smr, d_mat: our_mat - usp_mat };
    },
    hasSmeta() {
      const s = this.smeta;
      return !!(s.our_smr || s.our_mat || s.usp_smr || s.usp_mat);
    },
  },
  methods: {
    async copyGuid() {
      try {
        await navigator.clipboard.writeText(this.data.workGuid || '');
        this.$q?.notify?.({ type: 'positive', message: 'Скопировано' });
      } catch { /* ignore */ }
    },
  },
};
</script>

<style scoped>
.disc-smeta-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 1px solid #eceff1;
  background: #fafbfc;
  border-radius: 6px;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}
.disc-smeta-bar__lbl {
  font-weight: 600;
  color: #455a64;
  white-space: nowrap;
}
.disc-smeta-bar__cells {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  flex: 1;
}
.disc-smeta-bar__cell {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #eceff1;
}
.disc-smeta-bar__cell--ours { background: #f1f8e9; border-color: #dcedc8; }
.disc-smeta-bar__cell--usp  { background: #e3f2fd; border-color: #bbdefb; }
.disc-smeta-bar__cell--delta{ background: #fff3e0; border-color: #ffe0b2; }
.disc-smeta-bar__h {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #607d8b;
  font-weight: 700;
}
.disc-smeta-bar__v {
  color: #37474f;
}
.disc-smeta-bar__sep {
  margin: 0 6px;
  color: #b0bec5;
}

.disc-sent-count {
  display: inline-block;
  min-width: 22px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #eceff1;
  color: #546e7a;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.disc-sent-count--many {
  background: #ffe0b2;
  color: #ef6c00;
}

.disc-detail-bar {
  height: auto !important;
  min-height: 44px;
  padding: 4px 8px;
}
.disc-detail-titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 8px;
  flex: 1;
  line-height: 1.2;
}
.disc-detail-project {
  font-size: 11px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.disc-detail-work {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
